import _ from "lodash";
import update from "immutability-helper";
import { AESEncrypt, AESDecrypt } from "Utils/EncryptionHelpers";

const keys = {
  USER_INFO: "userInfo",
  RECENT_SEARCHES: "recentSearches",
  ORG_WORKSPACE_DETAILS: "orgWorkspaceDetails",
  USER_LOGGED_IN_TRIGGERED: "userLoggedInTriggered",
  SENTRAL_ACTIVITY: "sentralActivity",
  SAML_INITIATED_DETAILS: "samlInitiatedDetails",
};

//this is structure for storage initial state for recent search in local storage
export const recentSearchInitialState = {
  version: 4,
  data: {},
};

export const clearLocalStorage = () => {
  localStorage.clear();
};

export const clearLocalStorageForKey = key => {
  localStorage.removeItem(key);
};

export const getJsonItem = key => {
  try {
    const item = localStorage.getItem(key);
    if (!item) {
      return;
    }
    return JSON.parse(item);
  } catch (err) {
    console.error(err);
    return;
  }
};

export const getDecryptedJsonItem = key => {
  try {
    const item = localStorage.getItem(key);
    if (!item) {
      return;
    }
    const decryptedValue = AESDecrypt(item);
    return JSON.parse(decryptedValue);
  } catch (err) {
    console.error(err);
    return;
  }
};

/**
 * @param {Object} params
 * @param {string} params.key -
 * @param {*} params.value -
 */
export const setJsonItem = ({ key, value }) => {
  try {
    const serializedValue = JSON.stringify(value);
    localStorage.setItem(key, serializedValue);
  } catch (err) {
    console.error(err);
  }
};

/**
 * @param {Object} params
 * @param {string} params.key -
 * @param {*} params.value -
 */
export const setEncryptedJsonItem = ({ key, value }) => {
  try {
    const encryptedValue = AESEncrypt(JSON.stringify(value));
    localStorage.setItem(key, encryptedValue);
  } catch (err) {
    console.error(err);
  }
};

// *IMPORTANT* - use getUserInfo instead of this function
// local storage is only used for initial validation, redux is considered as the source of truth
export const getUserInfofromStorage = () => {
  return getJsonItem(keys.USER_INFO);
};

export const getOrgWorkSpaceDetailsFromStorage = () => {
  return getJsonItem(keys.ORG_WORKSPACE_DETAILS);
};

export const clearOrgWorkSpaceDetailsFromStorage = () => {
  clearLocalStorageForKey(keys.ORG_WORKSPACE_DETAILS);
};

export const setUserInfoInStorage = value => {
  setJsonItem({ key: keys.USER_INFO, value });
};

export const setIsUserLoginActivityLogged = value => {
  setJsonItem({ key: keys.USER_LOGGED_IN_TRIGGERED, value });
};

export const getIsUserLoginActivityLogged = () => {
  return getJsonItem(keys.USER_LOGGED_IN_TRIGGERED);
};

export const setIsSentralLoginActivityLogged = ({
  isLoginTriggered = false,
  openInNewTab = false,
  studentId = null,
  platformName = null,
  backUrl = null,
  studentProfileUrl = null,
  firstTimeLogin = false,
  curriculumId = null,
  adminUrl = null,
}) => {
  const sentralActivityData = {
    isLoginTriggered,
    openInNewTab,
    studentId,
    platformName,
    backUrl,
    studentProfileUrl,
    firstTimeLogin,
    curriculumId,
    adminUrl,
  };
  setJsonItem({ key: keys.SENTRAL_ACTIVITY, value: sentralActivityData });
};

export const getIsSentralLoginActivityLogged = () => {
  const data = getJsonItem(keys.SENTRAL_ACTIVITY);

  if (typeof data === "boolean") {
    return {
      isLoginTriggered: false,
      openInNewTab: false,
      studentId: null,
      platformName: null,
      backUrl: null,
      studentProfileUrl: null,
      firstTimeLogin: false,
      curriculumId: null,
      adminUrl: null,
    };
  }

  return (
    data || {
      isLoginTriggered: false,
      openInNewTab: false,
      studentId: null,
      platformName: null,
      backUrl: null,
      studentProfileUrl: null,
      firstTimeLogin: false,
      curriculumId: null,
      adminUrl: null,
    }
  );
};

export const setSamlInitiatedDetails = ({
  isInitiated = false,
  relayState = null,
  idpIdentificationId = null,
  requestInfo = null,
}) => {
  const samlInitiatedDetails = {
    isInitiated,
    relayState,
    idpIdentificationId,
    requestInfo,
  };
  setJsonItem({
    key: keys.SAML_INITIATED_DETAILS,
    value: samlInitiatedDetails,
  });
};

export const getSamlInitiatedDetails = () => {
  const data = getJsonItem(keys.SAML_INITIATED_DETAILS);
  return data || { isInitiated: false };
};

export const clearSamlInitiatedDetails = () => {
  clearLocalStorageForKey(keys.SAML_INITIATED_DETAILS);
};

export const setOrgWorkSpaceDetailsInStorage = value => {
  setJsonItem({ key: keys.ORG_WORKSPACE_DETAILS, value });
};

export const setRecentSearchesInStorage = value => {
  const updatedData = update(recentSearchInitialState, {
    data: { $set: value },
  });
  setJsonItem({ key: keys.RECENT_SEARCHES, value: updatedData });
};

export const getRecentSearchesFromStorage = () => {
  return getJsonItem(keys.RECENT_SEARCHES);
};

export const migrationFunction = () => {
  const recentItems = getRecentSearchesFromStorage();

  const currentVersion = _.get(recentItems, "version", 1);

  if (currentVersion != recentSearchInitialState.version) {
    clearLocalStorageForKey(keys.RECENT_SEARCHES);
  }
};

export const getRecentSearchesFromStorageByMigration = () => {
  //this function is match the version diff
  //there are some keys in global search which is not in prev. version
  //so that might break the module there for clear storage
  migrationFunction();

  return getRecentSearchesFromStorage();
};

export const getRecentSearches = ({
  userId,
  searchModuleName = "searches",
}) => {
  const recentSearches = getRecentSearchesFromStorageByMigration() || [];

  const currentUserSearches = _.get(
    recentSearches,
    `data.${userId}.${searchModuleName}`,
    []
  );

  //return total history , current history and index (necessary )
  return {
    currentUserSearches,
    recentSearchesHistory: _.get(recentSearches, "data", {}),
  };
};

export const changeLocalSearchStorageData = ({
  data,
  userId,
  searchModuleName = "searches",
  localSearchStorageCount = 25,
}) => {
  // Get current user searches
  const { currentUserSearches, recentSearchesHistory } = getRecentSearches({
    userId,
    searchModuleName,
  });

  // remove the element which has same id as new data in the user search history
  let updatedCurrentUserSearches = _.filter(
    currentUserSearches,
    ({ id }) => id != data.id
  );

  updatedCurrentUserSearches = _.concat([data], updatedCurrentUserSearches);

  const historyLength = _.get(updatedCurrentUserSearches, "length", 0);

  if (historyLength > localSearchStorageCount) {
    updatedCurrentUserSearches = _.initial(updatedCurrentUserSearches);
  }

  // If no history found for user, we will append new data
  // Else make change in the current data
  const updatedHistory = {
    ...recentSearchesHistory,
    [userId]: {
      ...recentSearchesHistory[userId],
      [searchModuleName]: updatedCurrentUserSearches,
    },
  };

  setRecentSearchesInStorage(updatedHistory);
};
